import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import facilityService from "../services/facilityService";
import fileRelationService from "../../../services/fileRelationService";
import FacilityHeaderCard from "../components/detail/FacilityHeaderCard";
import FacilityTabs from "../components/detail/FacilityTabs";
import ModelConfigurationTab from "../components/detail/ModelConfigurationTab";
import ContextFilesTab from "../components/detail/ContextFilesTab";
import BillingTab from "../components/detail/BillingTab";
import IntranetTab from "../components/detail/IntranetTab";
import BrandingTab from "../components/detail/BrandingTab";
import FileFormatsTab from "../components/detail/FileFormatsTab";
import FacilityDrawer from "../components/drawer/FacilityDrawer";
import { validateUserRole } from "../../../helpers/helpers";
import { userRoles } from "../../../helpers/constant";

const TABS = [
  { key: "models", label: "Model configuration" },
  { key: "files", label: "Context files" },
  { key: "billing", label: "Billing" },
  { key: "intranet", label: "Intranet" },
  { key: "branding", label: "Branding" },
  // Superadmin-only: its API answers 403 to other roles, which the axios
  // client treats as an expired session.
  { key: "file_formats", label: "File templates", superadminOnly: true },
];

const FacilityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [facility, setFacility] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("models");
  const [modelCount, setModelCount] = useState(null);
  const [fileCount, setFileCount] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState("details");

  const loadFacility = async () => {
    setIsLoading(true);
    try {
      const data = await facilityService.getFacilityById(id);
      setFacility(data);
    } catch (error) {
      console.error("Error fetching facility:", error);
      toast.error("Failed to load facility details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFacility();
  }, [id]);

  useEffect(() => {
    facilityService
      .getFacilityModelSettings()
      .then((data) => setModelCount(data.length))
      .catch(() => setModelCount(null));
  }, [id]);

  useEffect(() => {
    if (!facility?.facility_number) return;
    fileRelationService
      .getFileRelationsByFacility(facility.facility_number)
      .then((relations) => setFileCount((relations || []).length))
      .catch(() => setFileCount(null));
  }, [facility?.facility_number]);

  const handleToggleActive = async () => {
    try {
      const updated = await facilityService.updateFacility(facility.id, { is_active: !facility.is_active });
      setFacility((prev) => ({ ...prev, is_active: updated.is_active }));
      toast.success(updated.is_active ? "Facility activated." : "Facility deactivated.");
    } catch (error) {
      console.error("Error updating facility status:", error);
      toast.error("Failed to update facility status.");
    }
  };

  const isSuperAdmin = validateUserRole(userRoles.SUPERADMIN);

  const tabs = TABS.filter((tab) => !tab.superadminOnly || isSuperAdmin).map((tab) => ({
    ...tab,
    count: tab.key === "models" ? modelCount : tab.key === "files" ? fileCount : null,
  }));

  if (isLoading) {
    return <div className="py-16 text-center text-gray-500">Loading facility details...</div>;
  }

  if (!facility) {
    return <div className="py-16 text-center text-gray-500">Facility not found.</div>;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate("/facility")}
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to facilities
      </button>

      <FacilityHeaderCard
        facility={facility}
        onEdit={() => {
          setDrawerInitialTab("details");
          setDrawerOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />

      <div className="mt-6">
        <FacilityTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        <div className="rounded-2xl border border-gray-100 bg-white p-6">
          {activeTab === "models" && <ModelConfigurationTab />}
          {activeTab === "files" && <ContextFilesTab facilityNumber={facility.facility_number} />}
          {activeTab === "billing" && <BillingTab />}
          {activeTab === "intranet" && <IntranetTab facility={facility} />}
          {activeTab === "file_formats" && isSuperAdmin && <FileFormatsTab facility={facility} />}
          {activeTab === "branding" && (
            <BrandingTab
              facility={facility}
              onReplaceLogo={() => {
                setDrawerInitialTab("details");
                setDrawerOpen(true);
              }}
              onOpenThemeEditor={() => {
                setDrawerInitialTab("theme");
                setDrawerOpen(true);
              }}
            />
          )}
        </div>
      </div>

      {drawerOpen && (
        <FacilityDrawer
          facility={facility}
          initialTab={drawerInitialTab}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => loadFacility()}
        />
      )}
    </div>
  );
};

export default FacilityDetailPage;
